import React, { useCallback } from 'react'
import { useAtom } from 'jotai'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import ToggleIssueTypes from '../components/ToggleIssueTypes'
import { newsFiltersAtom } from '../../atoms/issueAtom'
import DropdownSelect from '../components/DropdownSelect'
import ToggleWatchLists from '../components/ToggleWatchLists'
import { TransactionType } from 'types/TransactionType'

const sourceTypeItems = [
    { id: 'cancellation', label: 'Avbruten' },
    { id: 'new commitments', label: 'Nya åtaganden' },
    { id: 'Guarantor fee issue', label: 'Garantiersättning' },
    { id: 'publication', label: 'Offentliggörande' },
    { id: 'prospectus', label: 'Prospekt' },
    { id: 'outcome', label: 'Utfall' },
    { id: 'terms', label: 'Villkor' },
]

type HandleChangeTypes = {
    issueTypes: Record<'value', Exclude<TransactionType, 'Guarantor fee issue'>[]>
    sourceType: Record<'value', string[]>
    onlyInWatchLists: Record<'value', boolean>
}

function handleChange<T extends keyof HandleChangeTypes>(
    type: T,
    setFilters: (cb: (v: HandleChangeTypes) => HandleChangeTypes) => void,
): (v: HandleChangeTypes[T]['value']) => void
function handleChange(
    type: 'sourceType',
    setFilters: (cb: (v: HandleChangeTypes) => HandleChangeTypes) => void,
): (v: string[]) => void {
    return (value: string[]) => {
        let newValue = Array.isArray(value) ? [...value] : value

        if (type === 'sourceType') {
            if (value.includes('prospectus')) {
                newValue.push('memorandum')
            } else {
                newValue = newValue.filter((v) => v !== 'memorandum')
            }
        }

        setFilters((filters: HandleChangeTypes) => ({ ...filters, [type]: { value: newValue } }))
    }
}

export const NewsFilter = () => {
    const [filters, setFilters] = useAtom(newsFiltersAtom)
    const handleChangeIssueType = useCallback(
        (value: Exclude<TransactionType, 'Guarantor fee issue'>[]) => handleChange('issueTypes', setFilters)(value),
        [setFilters],
    )
    const handleChangeSourceType = useCallback(
        (value: string[]) => handleChange('sourceType', setFilters)(value),
        [setFilters],
    )
    const handleChangeWatchLists = useCallback(
        (value: boolean) => handleChange('onlyInWatchLists', setFilters)(value),
        [setFilters],
    )

    const sortedSourceTypes = sourceTypeItems.sort((a, b) => a.label.localeCompare(b.label))

    return (
        <Grid container spacing={6} sx={{ mb: 2, pb: 4 }}>
            <Grid>
                <ToggleIssueTypes dense value={filters.issueTypes.value} onChange={handleChangeIssueType} />
            </Grid>
            <Grid minWidth={250} size={{ sm: 3, md: 2, lg: 1 }}>
                <Box sx={{ paddingBottom: 4 }}>
                    <DropdownSelect
                        label='Nyhetstyp'
                        items={sortedSourceTypes}
                        multiple
                        selectedItems={filters.sourceType.value}
                        onChange={handleChangeSourceType}
                    />
                </Box>
            </Grid>
            <Grid>
                <ToggleWatchLists onChange={handleChangeWatchLists} value={filters.onlyInWatchLists.value} />
            </Grid>
        </Grid>
    )
}
